// Where the ACH RECEIPT column links for one transfer, or null for no link.
//
// Since private payment docs (payup-api #978, 2026-09-18) PayUp no longer
// stores ach_receipt_url on new payments, so a column that only reads that
// field is blank for every ACH paid after it. The PDF still exists and the API
// serves it at /transfer/:id/receipt (a short-lived presigned redirect), which
// is the same link the PAID ON date already uses.
//
// The rule matches the vendor app and BillRoute: a stored receipt address, or
// an ACH transfer that is paid. Never link anything else — the receipt route
// writes a permanent PDF for whatever transfer it is asked about, so a check
// or an unpaid ACH would get a wrong receipt.
export type ReceiptTransfer = {
  id?: string | null;
  pay_mode?: string | null;
  status?: string | null;
  ach_receipt_url?: string | null;
} | null;

export const achReceiptHref = (transfer: ReceiptTransfer | undefined): string | null => {
  if (!transfer?.id) return null;
  const hasStoredReceipt = !!transfer.ach_receipt_url;
  const isPaidAch =
    String(transfer.pay_mode ?? '').toLowerCase() === 'ach' &&
    String(transfer.status ?? '').toLowerCase() === 'paid';
  if (!hasStoredReceipt && !isPaidAch) return null;
  return `/api/transfer/${encodeURIComponent(transfer.id)}/receipt`;
};

// The same receipt as a full address, for CSV exports: a file opened outside
// the dashboard needs the host. The link opens for anyone signed in to the
// dashboard (the API checks the transfer is on one of their properties).
export const achReceiptExportUrl = (
  transfer: ReceiptTransfer | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string | null => {
  const href = achReceiptHref(transfer);
  return href ? `${origin}${href}` : null;
};
